import styled from "@emotion/styled"
import { Accordion as MuiAccordion } from "@mui/material"
import AccordionSummary from "@mui/material/AccordionSummary"
import Image from "next/image"
import { useState } from "react"
import colors from "theme/colors"
import { NewText } from "ui"
import { extractProperty } from "utils"
import { httpsImage } from "utils/utils"

const Accordion = styled(MuiAccordion)({
  "&.MuiAccordion-root": {
    boxShadow: "none",
    "&:before": {
      // This targets the default divider line between closed accordions
      display: "none",
    },
    "& .MuiAccordionSummary-root": {
      padding: 0,
    },
    "& .MuiAccordionDetails-root": {
      padding: 0,
    },
  },
})

const StyledIconsContainer = styled.div`
  display: flex;
`
const StyledTextsContainer = styled.div`
  display: flex;
  flex-direction: column;
`
const AccContainer = styled.div<{ open: boolean }>`
  display: flex;
  align-items: center;
  gap: ${(props) => (props.open ? "16" : "40")}px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: ${(props) => (props.open ? "26" : "16")}px;
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    gap: ${(props) => (props.open ? "40" : "24")}px;
  }
`

const StyledIconContainer = styled.div`
  position: relative;
  width: 64px;
  height: 106px;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 84.5px;
    height: 139.5px;
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    width: 105px;
    height: 173.91px;
  }
`

const StyledIcon = styled.div`
  width: 40px;
  background: ${colors.darkGray};
  height: 4px;
`

const StyledContentText = styled(NewText)`
  p {
    margin: 0;
  }
`
type Props = { data: any; defaultOpen: boolean }

function AccordionItemWithIcon({ data, defaultOpen }: Props) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const handleAccordionChange = (
    event: React.ChangeEvent<{}>,
    expanded: boolean,
  ) => {
    setIsOpen(expanded)
  }
  const accordionTitle = extractProperty(data, "accordionTitle")
  const accordionContent = extractProperty(data, "accordionContent")
  const accordionImageIcon = extractProperty(data, "accordionImageIcon")

  return (
    <Accordion
      defaultExpanded={defaultOpen}
      onChange={handleAccordionChange}
      style={{ margin: 0 }}
    >
      <AccordionSummary aria-controls="panel1a-content" id="panel1a-header">
        <AccContainer open={isOpen}>
          <StyledIconsContainer>
            {!isOpen && <StyledIcon />}
            {accordionImageIcon && isOpen && (
              <StyledIconContainer>
                <Image
                  priority
                  src={httpsImage(accordionImageIcon)}
                  fill
                  style={{ objectFit: "contain" }}
                  alt=""
                />
              </StyledIconContainer>
            )}
          </StyledIconsContainer>
          <StyledTextsContainer>
            <NewText
              component={"p"}
              largeDesktopFontSize={36}
              desktopFontWeight={500}
              largeDesktopLineHeight={"50.4px"}
              color={isOpen ? colors.black : colors.darkGray}
              mobileFontSize={20}
              mobileLineHeight={"28px"}
              desktopFontSize={28}
              desktopLineHeight="39.2px"
            >
              {accordionTitle}
            </NewText>
            {isOpen && (
              <StyledContentText
                component={"div"}
                largeDesktopFontSize={28}
                desktopFontWeight={400}
                largeDesktopLineHeight={"39.2px"}
                color={colors.black}
                mobileFontSize={20}
                mobileLineHeight={"28px"}
                desktopFontSize={24}
                desktopLineHeight="28.8px"
                dangerouslySetInnerHTML={{ __html: accordionContent }}
              />
            )}
          </StyledTextsContainer>
        </AccContainer>
      </AccordionSummary>
    </Accordion>
  )
}

export default AccordionItemWithIcon
